/**
 * Ticket SK5: Öffentliche Übersicht der kaufbaren Kurse.
 *
 * Die Liste entsteht bei jedem Aufruf aus dem Katalog: ein kaufbar gesetzter
 * Kurs mit öffentlicher Adresse erscheint von selbst, ein deaktivierter
 * verschwindet. Nichts wird von Hand gepflegt.
 *
 * Ticket WS3: Die Seite ist freigegeben – Menüeintrag in der Gruppe „Wissen",
 * Eintrag in der Sitemap, keine Sperre für Suchmaschinen mehr. Die Kaufstrecke
 * `/kaufen/...` bleibt gesperrt.
 *
 * Kein Kaufknopf in der Übersicht – gekauft wird erst auf der Detailseite.
 */

import { createFileRoute } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { OfferList } from "@/components/offers/OfferList";
import { courseOfferItems } from "@/components/offers/offer-items";
import { listPublicCourses } from "@/lib/courses-public.functions";
import { COURSE_FORMAT_INTROS } from "@/lib/courses-public";
import { SERVICE_COURSE_FORMAT_LABELS } from "@/lib/service-format";

const TITLE = "Kurse für Wahlkampf und politische Kommunikation | creategy";
const DESCRIPTION =
  "Videokurse und Kompaktkurse von creategy: Wissen aus über 200 Wahlkämpfen, in Ihrem Tempo und jederzeit abrufbar.";

export const Route = createFileRoute("/wissen/kurse/")({
  loader: async () => ({ groups: await listPublicCourses() }),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  errorComponent: CoursesError,
  component: CoursesOverviewPage,
});

function CoursesError() {
  return (
    <Layout>
      <section>
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <h1 className="heading-h1">Kurse</h1>
          <p className="mt-4 max-w-prose font-body text-base text-muted-foreground">
            Die Übersicht ist im Moment nicht abrufbar. Bitte laden Sie die Seite noch einmal – oder
            sprechen Sie uns direkt an.
          </p>
        </div>
      </section>
    </Layout>
  );
}

const EMPTY_TEXT =
  "Derzeit ist kein Kurs buchbar. Sprechen Sie uns gern direkt an – wir sagen Ihnen, was zu Ihrem Vorhaben passt.";

function CoursesOverviewPage() {
  const { groups } = Route.useLoaderData();

  return (
    <Layout>
      <section>
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <header>
            <h1 className="heading-h1">Kurse</h1>
            <p className="mt-5 font-body text-base leading-relaxed text-muted-foreground">
              Praxiswissen für alle, die politisch kommunizieren, ob im Wahlkampf, im Amt oder in der Verwaltung.
            </p>
          </header>

          {groups.length === 0 ? (
            <p className="mt-12 max-w-prose font-body text-base text-muted-foreground">
              {EMPTY_TEXT}
            </p>
          ) : (
            <div className="mt-12 space-y-16 lg:mt-16 lg:space-y-24">
              {groups.map((group) => (
                <section key={group.format}>
                  <h2 className="heading-h2">{SERVICE_COURSE_FORMAT_LABELS[group.format]}</h2>
                  <p className="mt-3 font-body text-base leading-relaxed text-muted-foreground">
                    {COURSE_FORMAT_INTROS[group.format]}
                  </p>

                  {/* Ticket SK8: gemeinsamer Baustein mit der Downloads-Übersicht. */}
                  <OfferList
                    headingLevel="h3"
                    emptyText={EMPTY_TEXT}
                    items={courseOfferItems(group.items)}
                  />

                </section>
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
